import { memo, useCallback, useEffect, useRef, useState } from 'react';
import ContainerLayout from '@wconcept/wconcept-ui-renewal/components/layouts/ContainerLayout';
import ActionbarLayout from '@wconcept/wconcept-ui-renewal/components/layouts/ActionbarLayout';
import Header from '@wconcept/wconcept-ui-renewal/components/organisms/_header/Header';
import Footer from '@wconcept/wconcept-ui-renewal/components/organisms/Footer';
// import Actionbar from '@wconcept/wconcept-ui-renewal/components/organisms/Actionbar';
import MainGnbMenuBarItem from '@wconcept/wconcept-ui-renewal/components/molecules/_item/MainGnbMenuBarItem';
import MainGnbMenuBar from '@wconcept/wconcept-ui-renewal/src/components/organisms/_menuBar/MainGnbMenuBar';
import MenuBarSwiper from '@wconcept/wconcept-ui-renewal/components/atoms/MenuBarSwiper';
import SubGnbItem from '@wconcept/wconcept-ui-renewal/components/molecules/_item/SubGnbItem';
import useCommonMainLayoutStore from '@wconcept/wconcept-common/stores/event/commonMainLayoutStore';

import { classNameBind } from '@wconcept/wconcept-common/utils/commonUtils';
import {
  NewGnbInfo,
  SubMenuListItem,
} from '@wconcept/wconcept-common/models/renewal/gnb';
import { BiLogo } from '@wconcept/wconcept-common/models/renewal/popup';
import type { AgentOnBoardingYn } from '@wconcept/wconcept-common/models/renewal/datadog';
import * as S from './style';

interface IProps {
  headerBgType?: 'white' | 'black' | 'transparent' | undefined;
  gnbType?: 'default' | 'liquidGlass' | undefined;
  isLogin?: boolean;
  /** className */
  className?: string;
  /** children */
  children?: React.ReactNode;
  /** gnbList */
  gnbList?: NewGnbInfo[];
  /** selectedGnbValue */
  selectedGnbValue?: string;
  subGnbList?: SubMenuListItem[];
  selectedSubGnbValue?: string;
  /** isFooter */
  isFooter?: boolean;
  /** isNativeAppBoth */
  isNativeAppBoth?: boolean;
  /** isIPhoneXApp */
  isIPhoneXApp?: boolean;
  /** basketCount */
  basketCount?: number;
  /** isShortFormBtnTooltip */
  isShortFormBtnTooltip?: boolean;
  /** setTooltipShown */
  setTooltipShown?: (value: string) => void;
  /** historyImg */
  historyImg?: string;
  viewProductBtn?: React.ReactNode;
  appVersion?: string;
  logoInfo?: BiLogo;
  isHistoryBtn?: boolean;
  historyBtnRs?: string;
  isShareBtn?: boolean;
  eventType?: string;
  /** handleClickMenuBar */
  handleClickMenuBar?: (value: NewGnbInfo, no: number) => void;
  handleClickSubMenuBar?: (value: SubMenuListItem, no: number) => void;
  /** getHistoryImg */
  getHistoryImg?: () => void;
  /** onClickHome */
  onClickHome?: () => void;
  /** onClickCate */
  onClickCate?: () => void;
  /** onClickDiscovery */
  // onClickDiscovery?: () => void;
  /** onClickSearch */
  onClickSearch?: () => void;
  /** onClickMypage */
  onClickMypage?: () => void;
  /** onClickMyheart */
  onClickMyheart?: () => void;
  /** onClickHistory */
  onClickHistory?: () => void;
  /** onClickShare */
  onClickShare?: () => Promise<void>;
  /** onClickSearchBtn */
  onClickSearchBtn?: () => void;
  /** onClickCartBtn */
  onClickCartBtn?: () => void;
  onClickLogoBtn?: () => void;
  onClickStyleClipAddBtn?: () => void;
  onClickFooter?: (type: string) => void;
  agentOnBoardingYn?: AgentOnBoardingYn;
}

interface CustomScrollEvent extends Event {
  detail: {
    isCustomScrollRef: boolean;
    targetScrollTop?: number;
    headerScrollDirection?: 'scrollDown' | 'scrollUp';
  };
}

const MainLayout = ({
  headerBgType,
  gnbType,
  isLogin,
  className,
  children,
  gnbList,
  selectedGnbValue,
  subGnbList,
  selectedSubGnbValue,
  isFooter = true,
  isNativeAppBoth,
  isIPhoneXApp,
  basketCount,
  historyImg,
  isHistoryBtn,
  historyBtnRs,
  isShareBtn,
  viewProductBtn,
  isShortFormBtnTooltip,
  setTooltipShown,
  appVersion,
  logoInfo,
  eventType,
  getHistoryImg,
  handleClickMenuBar,
  handleClickSubMenuBar,
  onClickHome,
  onClickCate,
  // onClickDiscovery,
  onClickSearch,
  onClickMypage,
  onClickMyheart,
  onClickHistory,
  onClickShare,
  onClickCartBtn,
  onClickSearchBtn,
  onClickLogoBtn,
  onClickStyleClipAddBtn,
  onClickFooter,
  agentOnBoardingYn,
}: IProps) => {
  const { getIsLpNaviMove, setIsLpNaviMove } = useCommonMainLayoutStore();
  const [cName, setCName] = useState<string>(
    classNameBind([
      'main-layout',
      className || '',
      isNativeAppBoth ? 'is-app' : '',
    ])
  );

  const isCustomScrollRef = useRef(false);
  const isFixHeaderRef = useRef(false);
  const scrollYRef = useRef(0);
  const ticking = useRef(false);

  useEffect(() => {
    // 헤더 상태 계산
    const header = document.getElementById('header')?.getBoundingClientRect();
    const headerOfsTop = document.getElementById('header')?.offsetTop || 0;
    const headerHeight = header?.height || 0;

    const handleScroll = () => {
      if (!ticking.current) {
        ticking.current = true;
        requestAnimationFrame(() => {
          const scrollYPos = window.pageYOffset;

          if (getIsLpNaviMove()) {
            setIsLpNaviMove(false);
            ticking.current = false;
            return;
          }
          const scrollChange = scrollYPos - scrollYRef.current;
          if (Math.abs(scrollChange) < 1) {
            ticking.current = false;
            return;
          }
          if (isCustomScrollRef.current) {
            isCustomScrollRef.current = false;
            ticking.current = false;
            return;
          }

          const scDir = scrollChange > 0 ? 'scrollDown' : 'scrollUp';
          scrollYRef.current = scrollYPos;

          let isFixHeader = isFixHeaderRef.current;
          let isScrollDir = scDir;

          if (headerHeight + headerOfsTop < scrollYPos) {
            isFixHeader = true;
          } else if (scrollYPos <= headerOfsTop) {
            isFixHeader = false;
            isScrollDir = 'scrollTop';
          }

          isFixHeaderRef.current = isFixHeader;

          setCName(
            classNameBind([
              'main-layout',
              className || '',
              isNativeAppBoth ? 'is-app' : '',
              isFixHeader ? 'is-fix-header' : '',
              isScrollDir ? `is-${isScrollDir}` : '',
            ])
          );

          ticking.current = false;
        });
      }
    };

    const handleCustomScroll = (event: CustomScrollEvent) => {
      if (event.detail.isCustomScrollRef) {
        isCustomScrollRef.current = true;

        if (
          typeof event.detail.targetScrollTop === 'number' &&
          event.detail.headerScrollDirection
        ) {
          const { targetScrollTop } = event.detail;
          const isFixHeader = headerHeight + headerOfsTop < targetScrollTop;
          const scrollDirection =
            targetScrollTop <= headerOfsTop
              ? 'scrollTop'
              : event.detail.headerScrollDirection;

          scrollYRef.current = targetScrollTop;
          isFixHeaderRef.current = isFixHeader;
          setCName(
            classNameBind([
              'main-layout',
              className || '',
              isNativeAppBoth ? 'is-app' : '',
              isFixHeader ? 'is-fix-header' : '',
              `is-${scrollDirection}`,
            ])
          );
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('customScroll' as any, handleCustomScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('customScroll' as any, handleCustomScroll);
    };
  }, []);

  const getPathnameFromUrl = useCallback((url?: string) => {
    if (!url) return '';

    try {
      return new URL(url).pathname;
    } catch {
      return url.split('?')[0] || '';
    }
  }, []);

  /** 전문관(/rn/spc)은 하위 타입·트레일링 슬래시와 무관하게 동일 메뉴로 매칭 */
  const isMatchingGnbPath = useCallback(
    (menuPath: string, selectedPath: string): boolean => {
      if (!menuPath || !selectedPath) return false;
      if (menuPath === selectedPath) return true;

      const normalizePath = (path: string): string =>
        path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;

      const normalizedMenuPath = normalizePath(menuPath);
      const normalizedSelectedPath = normalizePath(selectedPath);
      if (normalizedMenuPath === normalizedSelectedPath) return true;

      const isSpcPath = (path: string): boolean =>
        path === '/rn/spc' || path.startsWith('/rn/spc/');

      return isSpcPath(normalizedMenuPath) && isSpcPath(normalizedSelectedPath);
    },
    []
  );

  const selectedSubGnbPath = getPathnameFromUrl(selectedSubGnbValue);
  const selectedGnbPath = getPathnameFromUrl(selectedGnbValue);
  const currentSelectedPath = selectedSubGnbPath || selectedGnbPath;
  const isWomenPath = currentSelectedPath === '/rn/women';
  const selectedSubMenu = gnbList
    ?.find(v =>
      v.subMenuList?.some(subMenu =>
        isMatchingGnbPath(
          getPathnameFromUrl(subMenu.landingUrl),
          currentSelectedPath
        )
      )
    )
    ?.subMenuList?.find(subMenu =>
      isMatchingGnbPath(
        getPathnameFromUrl(subMenu.landingUrl),
        currentSelectedPath
      )
    );
  const selectedParentId = String(
    selectedSubMenu?.gnbMenuId ??
      gnbList?.find(v =>
        isMatchingGnbPath(getPathnameFromUrl(v.landingUrl), selectedGnbPath)
      )?.id ??
      ''
  );

  // ITEM RENDERER : getMenuBar
  const getMenuBar = useCallback(
    () =>
      gnbList?.map((v: NewGnbInfo, i: number) => {
        const itemValue = String(v.subMenuList?.[0]?.gnbMenuId ?? v.id);
        const defaultImageUrl = v.highlightDefaultImageUrl;
        const activeImageUrl = v.highlightActiveImageUrl;
        // 홈: 선택 시 활성 에셋 / 비홈: 선택 시 기본 에셋 (MainGnbMenuBarItem active 분기)
        const selectedHighlightAssetUrl = isWomenPath
          ? activeImageUrl
          : defaultImageUrl;
        let imgSrc: string | undefined;
        if (v.highlightType === 'IMAGE') {
          imgSrc = activeImageUrl;
        }

        let lottieData: string | undefined;
        if (v.highlightType === 'LOTTIE_JSON') {
          lottieData = activeImageUrl;
        }
        return (
          <MainGnbMenuBarItem
            key={`${v?.menuTitle || ''}-${v?.landingUrl || ''}`}
            value={itemValue}
            selectedValue={selectedParentId}
            text={v.menuTitle}
            dot={v.highlightType === 'POINT'}
            textColor={v.highlightType === 'COLOR' ? v.highlightColor : ''}
            isAd={v.adYn === 'Y'}
            imgSrc={imgSrc}
            activeImgSrc={
              v.highlightType === 'IMAGE'
                ? selectedHighlightAssetUrl
                : undefined
            }
            lottieData={lottieData}
            activeLottieData={
              v.highlightType === 'LOTTIE_JSON'
                ? selectedHighlightAssetUrl
                : undefined
            }
            onClick={() => {
              handleClickMenuBar?.(v, i + 1);
            }}
          />
        );
      }),
    [gnbList, handleClickMenuBar, isWomenPath, selectedParentId]
  );
  // ITEM RENDERER : getSubMenuBar
  const getSubMenuBar = useCallback(
    () =>
      subGnbList?.map((v: SubMenuListItem, i: number) => (
        <SubGnbItem
          key={`${v.gnbMenuId}-${v.menuSort}-${v.landingUrl}`}
          value={getPathnameFromUrl(v.landingUrl)}
          selectedValue={selectedSubGnbValue}
          title={v.menuTitle}
          subTitle={v.menuSubTitle}
          imgSrc={v.menuImageUrl}
          textColor={v.textColor}
          bgColor={v.backgroundColor}
          onClick={() => {
            handleClickSubMenuBar?.(v, i + 1);
          }}
        />
      )),
    [getPathnameFromUrl, subGnbList, handleClickSubMenuBar, selectedSubGnbValue]
  );

  const getContainer = useCallback(
    () => (
      <ContainerLayout>
        {children}
        {viewProductBtn}
      </ContainerLayout>
    ),
    [children, viewProductBtn]
  );
  return (
    <S.MainLayout className={cName}>
      {!isNativeAppBoth ? (
        <>
          <div className="box-main-header">
            <div className="inner-box-main-header">
              <Header
                bgType={headerBgType}
                basketCount={basketCount}
                logoInfo={logoInfo}
                onClickSearchBtn={onClickSearchBtn}
                onClickCartBtn={onClickCartBtn}
                onClickLogoBtn={onClickLogoBtn}
              />
              <MainGnbMenuBar
                id="main-gnb-bar"
                items={getMenuBar()}
                selectedValue={selectedParentId}
                type={gnbType}
              />
              {subGnbList && (
                <MenuBarSwiper
                  className="sub-gnb-list"
                  selectedValue={selectedSubGnbValue}
                  items={getSubMenuBar()}
                />
              )}
            </div>
          </div>
          {getContainer()}
          <ActionbarLayout
            isNativeAppBoth={isNativeAppBoth || false}
            isIPhoneXApp={isIPhoneXApp || false}
            isShortFormBtnTooltip={isShortFormBtnTooltip}
            setTooltipShown={setTooltipShown}
            appVersion={appVersion || ''}
            historyImg={historyImg}
            onClickHome={onClickHome}
            onClickCate={onClickCate}
            onClickSearch={onClickSearch}
            onClickMypage={onClickMypage}
            onClickMyheart={onClickMyheart}
            getHistoryImg={getHistoryImg}
            onClickHistory={onClickHistory}
            onClickStyleClipAddBtn={onClickStyleClipAddBtn}
            onClickShare={onClickShare}
            isLogin={isLogin || false}
            eventType={eventType}
            historyBtnRs={historyBtnRs}
            isHistoryBtn={isHistoryBtn}
            isShareBtn={isShareBtn}
          />
        </>
      ) : (
        <>{getContainer()}</>
      )}

      {isFooter && (
        <Footer
          isLogin={isLogin || false}
          onClickFooter={onClickFooter}
          agentOnBoardingYn={agentOnBoardingYn}
        />
      )}
    </S.MainLayout>
  );
};

export default memo(MainLayout);
